import {
  activityKeysFromProfessionRefs,
  type ProfessionSlugRef,
  type SignupActivityKey,
  type SignupServiceMode,
} from './signupActivityCatalog';
import { PROFESSION_CATALOG } from './professionCatalog';
import {
  getOnboardingHintFromSignupActivity,
  getOnboardingLaneFromSignupActivity,
  readSignupActivitySession,
  SIGNUP_ACTIVITY_STORAGE_KEY,
  type OnboardingLane,
  type OnboardingSignupHint,
  type SignupActivitySessionV1,
} from './signupActivitySession';

export type ServiceWizardMode = 'event' | 'availability';

export type ServiceActionPathOptions = {
  /** Module pension déjà payé / activé — CTA va au planning, pas au checkout. */
  isBoardingEnabled?: boolean;
};

/** Tous les modes (mixed = stays + un métier de services). */
export function modesFromActivities(
  activities: readonly SignupActivityKey[]
): SignupServiceMode[] {
  const modes: SignupServiceMode[] = [];
  if (activities.includes('grooming')) modes.push('grooming');
  if (activities.includes('pet_sitting')) modes.push('sitting');
  if (
    activities.some((a) =>
      ['education', 'behavior', 'walking', 'leisure_sport'].includes(a)
    )
  ) {
    modes.push('sessions');
  }
  if (activities.includes('boarding')) modes.push('stays');
  return modes;
}

/**
 * Infère un mode soft pour le *goût* du 1er service (fallback sans sessionStorage).
 * Mixed boarding + séances/sitting/grooming : l’autre métier gagne (hint wizard).
 * Pension-only → stays.
 */
export function inferModeFromActivities(
  activities: readonly SignupActivityKey[]
): SignupServiceMode | null {
  const modes = modesFromActivities(activities);
  return modes.find((m) => m !== 'stays') ?? modes[0] ?? null;
}

function sessionFromProfessionIds(
  professions: readonly ProfessionSlugRef[],
  professionIds: readonly number[]
): SignupActivitySessionV1 | null {
  if (!professions.length || !professionIds.length) return null;
  const activities = activityKeysFromProfessionRefs(professions, professionIds);
  const modes = modesFromActivities(activities);
  return {
    modes,
    mode: modes[0] ?? null,
    activities,
    species: ['dog'],
    profession_ids: [...professionIds],
  };
}

type OnboardingResolveParams = {
  session?: SignupActivitySessionV1 | null;
  professions?: readonly ProfessionSlugRef[];
  professionIds?: readonly number[];
};

/**
 * Branche onboarding durable :
 * 1) sessionStorage signup_activity_v1
 * 2) sinon profession_ids + slugs API
 */
export function resolveOnboardingBranch(
  params?: OnboardingResolveParams
): OnboardingSignupHint {
  const session =
    params?.session !== undefined
      ? params.session
      : readSignupActivitySession();
  if (session) {
    return getOnboardingHintFromSignupActivity(session);
  }

  const fallback = sessionFromProfessionIds(
    params?.professions || [],
    params?.professionIds || []
  );
  return fallback ? getOnboardingHintFromSignupActivity(fallback) : 'default';
}

/** Les 3 parcours : services | mixed | stays (default = services-like). */
export function resolveOnboardingLane(
  params?: OnboardingResolveParams
): OnboardingLane {
  const session =
    params?.session !== undefined
      ? params.session
      : readSignupActivitySession();
  if (session) {
    return getOnboardingLaneFromSignupActivity(session);
  }

  const fallback = sessionFromProfessionIds(
    params?.professions || [],
    params?.professionIds || []
  );
  return fallback ? getOnboardingLaneFromSignupActivity(fallback) : 'default';
}

/** Payload signup valide (activities + species) — le JSON `{ profession_ids }` seul est rejeté. */
export function buildSignupActivitySessionFromProfessionIds(
  professionIds: readonly number[],
  professions: readonly ProfessionSlugRef[] = PROFESSION_CATALOG
): SignupActivitySessionV1 | null {
  return sessionFromProfessionIds(professions, professionIds);
}

export function persistSignupActivitySessionFromProfessionIds(
  professionIds: readonly number[],
  professions: readonly ProfessionSlugRef[] = PROFESSION_CATALOG
): void {
  const session = buildSignupActivitySessionFromProfessionIds(
    professionIds,
    professions
  );
  if (!session) return;
  try {
    sessionStorage.setItem(SIGNUP_ACTIVITY_STORAGE_KEY, JSON.stringify(session));
  } catch {
    /* ignore */
  }
}

/** Chemin CTA 1er cran (service catalogue, ou module pension). */
export function serviceActionPathForHint(
  hint: OnboardingSignupHint,
  options?: ServiceActionPathOptions
): string {
  if (hint === 'stays') {
    return options?.isBoardingEnabled
      ? '/app/pension?tab=config&setup=1'
      : '/app/settings?focus=boarding';
  }
  if (hint === 'sessions') {
    return '/app/services?action=add&mode=event';
  }
  if (hint === 'sitting' || hint === 'grooming') {
    return '/app/services?action=add&mode=availability';
  }
  return '/app/services?action=add';
}

export function serviceWizardModeForHint(
  hint: OnboardingSignupHint
): ServiceWizardMode | null {
  if (hint === 'sessions') return 'event';
  if (hint === 'sitting' || hint === 'grooming') return 'availability';
  return null;
}

/** Clés i18n relatives à startupPage.steps.service* (suffixe par hint). */
export function serviceStepI18nSuffix(hint: OnboardingSignupHint): string {
  switch (hint) {
    case 'sessions':
      return 'serviceSessions';
    case 'sitting':
      return 'serviceSitting';
    case 'grooming':
      return 'serviceGrooming';
    case 'stays':
      return 'serviceBoarding';
    default:
      return 'service';
  }
}

/** Étape « 1er cran » Démarrage : service catalogue, ou module pension activé. */
export function isOnboardingServiceStepDone(params: {
  hint: OnboardingSignupHint;
  servicesCount: number;
  isBoardingEnabled?: boolean;
}): boolean {
  if (params.hint === 'stays') {
    return Boolean(params.isBoardingEnabled);
  }
  return params.servicesCount > 0;
}
